<template>
  <v-app class="hosting-app">
    <!-- ===================================================== -->
    <!-- HEADER -->
    <!-- ===================================================== -->

    <HostingHeader
      :pinned="isPinned"
      @toggle-sidebar="toggleSidebar"
      @toggle-pin="togglePin"
    />

    <!-- ===================================================== -->
    <!-- MAIN AREA -->
    <!-- ===================================================== -->

    <div class="hosting-shell">
      <!-- =================================================== -->
      <!-- DESKTOP SIDEBAR -->
      <!-- =================================================== -->

      <aside
        v-if="mdAndUp && sidebarOpen"
        class="hosting-sidebar-desktop"
      >
        <HsSidebar />
      </aside>

      <!-- =================================================== -->
      <!-- MOBILE SIDEBAR -->
      <!-- =================================================== -->

      <v-navigation-drawer
        v-if="!mdAndUp"
        v-model="sidebarOpen"
        temporary
        width="260"
        class="hosting-navigation-drawer"
      >
        <HsSidebar />
      </v-navigation-drawer>

      <!-- =================================================== -->
      <!-- ROUTER CONTENT -->
      <!-- =================================================== -->

      <main class="hosting-main">
        <router-view />
      </main>
    </div>

    <!-- ===================================================== -->
    <!-- FOOTER -->
    <!-- ===================================================== -->

    <HostingFooter />
  </v-app>
</template>

<script setup>
import {
  ref,
  watch
} from 'vue'

import {
  useDisplay
} from 'vuetify'

import HostingHeader
  from '@/modules/hosting/components/HostingHeader.vue'

import HsSidebar
  from '@/modules/hosting/components/HsSidebar.vue'

import HostingFooter
  from '@/modules/hosting/components/HostingFooter.vue'

// ============================================================
// DISPLAY
// ============================================================

const {
  mdAndUp
} = useDisplay()

// ============================================================
// SIDEBAR STATE
// ============================================================

const sidebarOpen =
  ref(true)

const isPinned =
  ref(true)

// ============================================================
// TOGGLE SIDEBAR
// ============================================================

function toggleSidebar() {
  sidebarOpen.value =
    !sidebarOpen.value
}

// ============================================================
// PIN SIDEBAR
// ============================================================

function togglePin() {
  isPinned.value =
    !isPinned.value

  /*
   * Pinning the sidebar always makes it visible.
   */
  if (isPinned.value) {
    sidebarOpen.value =
      true
  }
}

// ============================================================
// RESPONSIVE SIDEBAR
// ============================================================

watch(
  mdAndUp,

  desktop => {
    if (desktop) {
      /*
       * Desktop starts with sidebar visible.
       */
      sidebarOpen.value =
        true
    }
    else {
      /*
       * Mobile starts with the temporary
       * drawer closed.
       */
      sidebarOpen.value =
        false
    }
  },

  {
    immediate: true
  }
)
</script>

<style scoped>
/* ============================================================
   APPLICATION
============================================================ */

.hosting-app {
  min-height: 100vh;
}

/*
 * Vuetify places the contents of v-app inside
 * .v-application__wrap.
 *
 * This is the important rule that gives us:
 *
 * Header
 * Main area grows
 * Footer stays at bottom
 */
.hosting-app :deep(.v-application__wrap) {
  min-height: 100vh;

  display: flex;
  flex-direction: column;
}

/* ============================================================
   HEADER / SIDEBAR / CONTENT AREA
============================================================ */

.hosting-shell {
  width: 100%;

  flex: 1 1 auto;

  display: flex;

  position: relative;

  /*
   * Important in flex layouts.
   * Prevents children from forcing
   * the container beyond the viewport.
   */
  min-height: 0;

  min-width: 0;
}

/* ============================================================
   DESKTOP SIDEBAR
============================================================ */

.hosting-sidebar-desktop {
  width: 260px;
  min-width: 260px;

  flex: 0 0 260px;

  align-self: stretch;

  background: #303030;

  /*
   * Sidebar now begins below the header
   * and ends immediately above the footer.
   */
  overflow: visible;
}

/*
 * Make the sidebar component itself occupy
 * the entire available middle-section height.
 */
.hosting-sidebar-desktop :deep(.hosting-sidebar) {
  min-height: 100%;
}

/* ============================================================
   MOBILE DRAWER
============================================================ */

.hosting-navigation-drawer {
  /*
   * Mobile uses Vuetify's temporary drawer.
   * Do NOT force position:relative here.
   * Vuetify needs to position its overlay/drawer.
   */
  z-index: 2000;
}

/* ============================================================
   MAIN CONTENT
============================================================ */

.hosting-main {
  flex: 1 1 auto;

  min-width: 0;
  min-height: 0;

  width: 100%;

  overflow-x: hidden;

  background: #ffffff;
}

/* ============================================================
   FOOTER
============================================================ */

:deep(.hosting-support-footer) {
  flex: 0 0 auto;

  /*
   * Prevent the footer from shrinking when
   * the central page becomes large.
   */
  flex-shrink: 0;
}

/* ============================================================
   RESPONSIVE
============================================================ */

@media (max-width: 959px) {
  .hosting-main {
    width: 100%;
  }
}
</style>